import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useMemo, useRef, useState } from "react";
import { fetchJson, getOptions, HttpMethod } from "../../api/api";
import { useAuth } from "@clerk/clerk-react";
import "./Watchlist.css";
import { captureErrorWithContext } from "../../utils/sentry";
import Fuse from "fuse.js";
import X from "../../components/Icons/X";
import Spinner from "../../components/Icons/Spinner";
import BackButton from "../../components/Icons/BackButton";
import { UserMessageLevels } from "../../views/UpdaterForm/types";
import { TextInput } from "../../components/Form/TextInput";
import { ICON_SIZES } from "../../components/Icons/IconSizes";
const WatchList = ({ companies, onBackButtonClick }) => {
    const fuse = useMemo(() => {
        const keys = ["ticker", "name"];
        return new Fuse(companies, {
            keys: keys,
            threshold: 0.3, // controls fuzziness
            includeMatches: true, // for highlighting
        });
    }, [companies]);
    const { getToken } = useAuth();
    const [watchedCompanies, setWatchedCompanies] = useState([]);
    const [fetchingWatchedCompanies, setFetchingWatchedCompanies] = useState(false);
    const debounceTimeoutRef = useRef(undefined);
    const [autoCompleteResults, setAutoCompleteResults] = useState([]);
    const [showAutoCompleteResults, setShowAutoCompleteResults] = useState(false);
    const sortedWatchedCompanies = useMemo(() => [...watchedCompanies].sort((a, b) => a.company.ticker.localeCompare(b.company.ticker)), [watchedCompanies]);
    const [ticker, setTicker] = useState("");
    const [watchedCompanyBeingDeleted, setWatchedCompanyBeingDeleted] = useState(null);
    const [userMessage, setUserMessage] = useState(null);
    async function fetchWatchedCompanies() {
        // Start spinner
        setFetchingWatchedCompanies(true);
        let options;
        try {
            options = await getOptions({
                getToken: getToken,
                method: HttpMethod.get,
            });
        }
        catch (error) {
            captureErrorWithContext(error, {
                extra: { note: "Failed in fetchWatchedCompanies()" },
                tags: { priority: "high" },
            });
            return false;
        }
        const response = await fetchJson("/watched-companies", options);
        // Stop spinner
        setFetchingWatchedCompanies(false);
        if ("error" in response) {
            // API call returned an error
            captureErrorWithContext(new Error(response.error), {
                extra: {
                    note: "Fetch failed in fetchWatchedCompanies()",
                },
                tags: {
                    priority: "high",
                },
            });
            return false;
        }
        setWatchedCompanies(response.data.watched_companies);
    }
    const addWatchedCompany = async (company) => {
        setShowAutoCompleteResults(false);
        setAutoCompleteResults([]);
        // Clear ticker
        setTicker("");
        const payload = { company_id: company.id };
        const options = await getOptions({
            getToken: getToken,
            tokenOptions: { template: "user-email" },
            method: HttpMethod.post,
            body: payload,
        });
        const response = await fetchJson("/watched-company", options);
        if ("error" in response) {
            // API call returned an error
            captureErrorWithContext(new Error(response.error), {
                extra: {
                    note: "Fetch failed in addWatchList",
                },
                tags: {
                    priority: "high",
                },
            });
            setUserMessage({
                message: `Failed to watch ${company.ticker}. ${response.error}`,
                level: UserMessageLevels.error,
            });
            return false;
        }
        const newWatchedCompany = {
            id: response.data.id,
            company: company,
        };
        setWatchedCompanies((previousWatchedCompanies) => [
            ...previousWatchedCompanies,
            ...[newWatchedCompany],
        ]);
        setUserMessage({
            message: `Now watching company ${company.ticker}`,
            level: UserMessageLevels.success,
        });
    };
    const deleteWatchedCompany = async (watched_company_id) => {
        setWatchedCompanyBeingDeleted(watched_company_id);
        const options = await getOptions({
            getToken: getToken,
            method: HttpMethod.delete,
        });
        const response = await fetchJson(`/watched-company/${watched_company_id}`, options);
        if ("error" in response) {
            // API call returned an error
            captureErrorWithContext(new Error(response.error), {
                extra: {
                    note: "Fetch failed in deleteWatchedCompany()",
                },
                tags: {
                    priority: "high",
                },
            });
            setWatchedCompanyBeingDeleted(null);
            return false;
        }
        setWatchedCompanies((previousWatchedCompanies) => previousWatchedCompanies.filter((watched_company) => watched_company.id !== watched_company_id));
        setWatchedCompanyBeingDeleted(null);
    };
    useEffect(() => {
        fetchWatchedCompanies();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    const companyIsBeingWatched = useMemo(() => {
        const watchedIds = new Set(watchedCompanies.map((w) => w.company.id));
        const cache = new Map();
        return (company) => {
            if (cache.has(company.id)) {
                return cache.get(company.id);
            }
            const isWatched = watchedIds.has(company.id);
            cache.set(company.id, isWatched);
            return isWatched;
        };
    }, [watchedCompanies]);
    const handleTickerChange = (e) => {
        if (debounceTimeoutRef.current) {
            clearTimeout(debounceTimeoutRef.current);
        }
        const newTicker = e.target.value.trim();
        setTicker(newTicker);
        debounceTimeoutRef.current = window.setTimeout(() => {
            if (!newTicker || newTicker == "") {
                // Clear and hide our auto complete results
                setAutoCompleteResults([]);
                setShowAutoCompleteResults(false);
                return;
            }
            // Only get the top 5 matches
            const results = fuse ? fuse.search(newTicker).slice(0, 5) : [];
            setAutoCompleteResults(results);
            setShowAutoCompleteResults(true);
        }, 200);
    };
    return (_jsxs("div", { id: "watchlist-wrapper", className: "h-screen overflow-hidden flex flex-col", children: [onBackButtonClick && (_jsx("div", { id: "back-button", className: "self-start mt-1", children: _jsx(BackButton, { onClick: onBackButtonClick }) })), _jsxs("div", { className: "w-full max-w-3xl px-1 mx-auto", children: [_jsxs("div", { id: "form-container", className: "mt-1", children: [_jsx("form", { id: "main-form", children: _jsxs("div", { className: "form-control w-full", children: [_jsx("div", { className: "label", children: _jsx("span", { className: "label-text", children: "Watchlist" }) }), _jsxs("label", { className: "input input-bordered flex items-center gap-2 relative", children: [_jsx("span", { className: "flex-none", children: "Ticker" }), _jsx(TextInput, { type: "text", className: "grow", placeholder: "AAPL", id: "ticker", name: "ticker", "aria-label": "Ticker", value: ticker, onChange: handleTickerChange, onFocus: () => {
                                                        ticker ? setShowAutoCompleteResults(true) : null;
                                                    }, onBlur: () => {
                                                        setShowAutoCompleteResults(false);
                                                    } }), showAutoCompleteResults && (_jsx("div", { id: "autocomplete-results", children: autoCompleteResults.length > 0 ? (autoCompleteResults.map((r) => (_jsxs("div", { className: `autocomplete-item text-sm ${companyIsBeingWatched(r.item) ? "disabled" : ""}`, "data-ticker": r.item.ticker, tabIndex: 0, onMouseDown: () => {
                                                            if (!companyIsBeingWatched(r.item)) {
                                                                addWatchedCompany(r.item);
                                                            }
                                                        }, onKeyDown: (e) => {
                                                            if (e.key === "Enter" || e.key === " ") {
                                                                e.preventDefault();
                                                                if (!companyIsBeingWatched(r.item)) {
                                                                    addWatchedCompany(r.item);
                                                                }
                                                            }
                                                        }, children: [_jsx("span", { className: `font-bold ${companyIsBeingWatched(r.item) ? "text-gray-400" : "text-blue-500"}`, children: r.item.ticker }), " ", _jsx("span", { className: "text-gray-400", children: r.item.name ?? "" })] }, r.refIndex)))) : (_jsx("div", { className: "autocomplete-no-results", children: _jsx("strong", { children: "No results" }) })) }))] })] }) }), userMessage && (_jsx("div", { className: "max-w-full mx-2", children: _jsx("p", { id: "userMessage", className: `pt-1 break-words text-xs whitespace-pre-line text-${userMessage.level}`, children: userMessage.message }) }))] }), _jsx("div", { id: "watched-companies-wrapper", className: "flex-1", children: _jsxs("div", { id: "watched-companies", className: "\n              grid\n              grid-cols-[auto_1fr_25px]\n              gap-y-2\n              p-1\n              mt-2\n              h-[calc(100vh-215px)]\n              overflow-y-auto\n              content-start\n            ", children: [fetchingWatchedCompanies && (_jsx("div", { className: "col-span-3 flex justify-center py-4", children: _jsx(Spinner, { size: ICON_SIZES.jumbo, color: "blue-500" }) })), sortedWatchedCompanies.map((watched_company) => (_jsxs("div", { className: "\n                  grid\n                  grid-cols-subgrid\n                  col-span-3\n                  gap-3\n                  border-b border-gray-300\n                ", children: [_jsx("span", { className: `font-bold ${watchedCompanyBeingDeleted === watched_company.id ? "text-gray-400" : "text-blue-500"} text-left`, children: watched_company.company.ticker }), _jsx("span", { className: "text-gray-400 text-left", children: watched_company.company.name ?? "" }), watchedCompanyBeingDeleted === watched_company.id && (_jsx("div", { className: "\n                      flex\n                      items-end\n                      text-red-600\n                    ", children: _jsx(Spinner, { size: ICON_SIZES.medium, color: "red" }) })), watchedCompanyBeingDeleted !== watched_company.id && (_jsx("button", { className: "\n                      flex\n                      items-end\n                      text-red-600\n                      hover:opacity-70\n                    ", "aria-label": `Delete Watched Company ${watched_company.company.ticker}`, onClick: () => deleteWatchedCompany(watched_company.id), children: _jsx(X, { size: ICON_SIZES.medium }) }))] }, watched_company.company.ticker)))] }) })] })] }));
};
export default WatchList;
